import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Page, alert } from 'equasion-lib';
import { FadeLoader } from 'react-spinners';
import { FiAlertTriangle, FiCheckCircle, FiRotateCcw, FiXCircle } from 'react-icons/fi';
import SharedScanner from '@/lib/shared-coms/SharedScanner';
import WarehouseProductDisplay from '@/lib/shared-coms/WarehouseProductDisplay';
import { SCAN_PROFILE } from '@/root/App/com/ScannerProvider/ScannerProvider';
import type { ScanResult } from '@/lib/scanning/scanResult';
import { playFailTone, playSuccessTone } from '@/lib/scanning/feedback';
import { operatorFacingMessage } from '@/lib/utils/errorUtils';
import { eqProps } from '@/pages/PurchaseOrderReceive/eqProps';
import { rmaReceiveScanFromResult, type RmaReceiveScanInput } from './rmaScan';
import '@/pages/PurchaseOrderReceive/PurchaseOrderReceive.scss';
import './ReturnReceive.scss';

const REARM_DELAY_MS = 250;

type ReceivingLine = {
  itemId: string;
  product?: any;
  rmaQuantity: number;
  scannedQuantity: number;
  receivedQuantity?: number | null;
  shortQuantity: number;
  serialized: boolean;
  shippedLots: string[];
};

type ReceivingScan = {
  id: string;
  itemId: string;
  serialNumber?: string | null;
  gtin?: string | null;
  code?: string | null;
  lot?: string | null;
  quantity: number;
};

type ReturnReceiveProps = {
  clientReturn: {
    clientReturnNumber: string;
    status: string;
    statusLabel?: string | null;
    receivingScanRequired?: boolean | null;
    receivingShortfall?: boolean | null;
    client?: { companyName?: string | null } | null;
    originalOrder?: { vendorSalesOrderNumber?: string | null } | null;
    receivingLines: ReceivingLine[];
    receivingScans: ReceivingScan[];
  };
  onScan: (scan: RmaReceiveScanInput) => Promise<unknown>;
  onRemoveScan: (scanId: string) => Promise<unknown>;
  onComplete: (note: string | null) => Promise<unknown>;
};

const lineName = (line?: ReceivingLine) =>
  line?.product?.title || line?.product?.fdaProduct?.name || 'Product';

/**
 * Receive an RMA's product in the warehouse — opened by scanning the RMA # barcode on its document
 * on the home screen. Every scan is checked by the server against the RMA and the original
 * shipment; a refused one says why and is not counted. Complete Receive takes what was scanned
 * (fewer than the RMA is allowed, and flagged) and creates the return order, which is then received
 * and stocked like any purchase order.
 */
function ReturnReceive({ clientReturn, onScan, onRemoveScan, onComplete }: ReturnReceiveProps) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [manualQuantity, setManualQuantity] = useState('1');
  const [manualLot, setManualLot] = useState('');
  const [manualSerial, setManualSerial] = useState('');
  const inFlightRef = useRef(false);

  const awaiting = clientReturn.status === 'rma_issued' && !!clientReturn.receivingScanRequired;
  const lines = clientReturn.receivingLines || [];
  const scans = clientReturn.receivingScans || [];
  const lineById = new Map(lines.map((line) => [line.itemId, line]));
  const totalRma = lines.reduce((sum, line) => sum + line.rmaQuantity, 0);
  const totalScanned = lines.reduce((sum, line) => sum + line.scannedQuantity, 0);
  const shortLines = lines.filter((line) => line.scannedQuantity < line.rmaQuantity);
  const rma = clientReturn.clientReturnNumber;

  const submit = async (scan: RmaReceiveScanInput | null) => {
    if (!scan || !awaiting || inFlightRef.current || busy || confirmOpen) return;
    inFlightRef.current = true;
    try {
      await onScan(scan);
      playSuccessTone();
    } catch (error) {
      playFailTone();
      alert(
        'Not accepted',
        operatorFacingMessage(error, 'Could not check that scan. Check the connection and scan it again.'),
        7,
        'error'
      );
    } finally {
      inFlightRef.current = false;
    }
  };

  const submitManual = () => {
    const serialNumber = manualSerial.trim();
    const code = manualCode.trim();
    if (!code && !serialNumber) return;
    const quantity = parseInt(manualQuantity, 10);
    void submit({
      ...(serialNumber ? { serialNumber, gtin: code || null } : { code }),
      lot: manualLot.trim() || null,
      quantity: !serialNumber && Number.isInteger(quantity) && quantity > 1 ? quantity : null
    });
    setManualCode('');
    setManualSerial('');
    setManualQuantity('1');
    setManualLot('');
  };

  const remove = async (scanId: string) => {
    try {
      await onRemoveScan(scanId);
    } catch (error) {
      alert('Could not undo', operatorFacingMessage(error, 'Try again.'), 6, 'error');
    }
  };

  const complete = async () => {
    if (busy || totalScanned === 0) return;
    setBusy(true);
    try {
      await onComplete(null);
      setConfirmOpen(false);
      setCompleted(true);
    } catch (error) {
      alert('Could not complete receive', operatorFacingMessage(error, 'Try again.'), 6, 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Page className="purchase-order-receive return-receive action-page">
      <div className="action-top-bar">
        <div className="order-details">
          <h1>Receive Return — RMA #{rma}</h1>
          <h2>
            {totalScanned} of {totalRma} units scanned
            {clientReturn.client?.companyName ? ` - ${clientReturn.client.companyName}` : ''}
          </h2>
        </div>
        <FiXCircle aria-label="Close" onClick={() => navigate('/')} />
      </div>

      <div className="action-page-body">
        {awaiting ? (
          <div className="receive-camera">
            <SharedScanner
              showSSCC
              scanProfile={SCAN_PROFILE.HOME}
              silentDecode
              rearmDelayMs={REARM_DELAY_MS}
              replaceManualEntry
              alternativeManualEntry={
                <div className="receive-manual">
                  <strong>Enter a product that will not scan</strong>
                  <Input
                    {...eqProps({
                      form: false,
                      label: 'NDC, UPC or GTIN',
                      value: manualCode,
                      onChange: ({ value }: { value: string }) => setManualCode(value)
                    })}
                  />
                  <Input
                    {...eqProps({
                      form: false,
                      label: 'Serial number (serialized product)',
                      value: manualSerial,
                      onChange: ({ value }: { value: string }) => setManualSerial(value)
                    })}
                  />
                  <Input
                    {...eqProps({
                      form: false,
                      label: 'Quantity (unserialized product)',
                      value: manualQuantity,
                      onChange: ({ value }: { value: string }) => setManualQuantity(value)
                    })}
                  />
                  <Input
                    {...eqProps({
                      form: false,
                      label: 'Lot (optional)',
                      value: manualLot,
                      onChange: ({ value }: { value: string }) => setManualLot(value)
                    })}
                  />
                  <Button
                    type="primary"
                    fullWidth
                    disabled={!manualCode.trim() && !manualSerial.trim()}
                    {...eqProps({ onClick: submitManual })}
                  >
                    Check product
                  </Button>
                </div>
              }
              handleScan={(result: ScanResult) => void submit(rmaReceiveScanFromResult(result))}
            />
          </div>
        ) : (
          <div className="return-receive__notice">
            <p>
              RMA #{rma} is {clientReturn.statusLabel || clientReturn.status}.{' '}
              {clientReturn.receivingScanRequired
                ? 'Its product has already been scanned in.'
                : 'It is not scanned in here.'}{' '}
              {clientReturn.status === 'receiving'
                ? 'Receive and stock its return order as a purchase order.'
                : ''}
            </p>
          </div>
        )}

        <div className="receive-summary">
          <div className="receive-summary__count">
            <span className="receive-summary__number">{totalScanned}</span>
            <span className="receive-summary__of">/ {totalRma}</span>
          </div>
          <div className="receive-summary__label">
            <span className="receive-summary__heading">
              Units scanned
              {awaiting && totalRma - totalScanned > 0 && (
                <span className="receive-summary__togo">{totalRma - totalScanned} to scan</span>
              )}
            </span>
            {clientReturn.originalOrder?.vendorSalesOrderNumber && (
              <small>SO #{clientReturn.originalOrder.vendorSalesOrderNumber}</small>
            )}
          </div>
        </div>

        <div className="receive-lines">
          {lines.map((line) => {
            const done = line.scannedQuantity >= line.rmaQuantity;
            return (
              <div
                key={line.itemId}
                className={['receive-line', done && 'receive-line--confirmed'].filter(Boolean).join(' ')}
              >
                <div className="receive-line__main">
                  <div className="receive-line__product">
                    <WarehouseProductDisplay product={line.product} />
                  </div>
                  <div className="receive-line__side">
                    <span className="receive-line__qty">
                      <small>{line.serialized ? 'Scan each serial' : 'Scanned'}</small>
                      {line.scannedQuantity}
                      <small className="receive-line__ordered">of {line.rmaQuantity}</small>
                    </span>
                  </div>
                </div>
                {line.shippedLots.length > 0 && (
                  <div className="return-receive__lots">Shipped lot {line.shippedLots.join(', ')}</div>
                )}
                {!awaiting && line.shortQuantity > 0 && (
                  <div className="receive-line__overage">
                    <FiAlertTriangle aria-hidden /> Received short by {line.shortQuantity}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {awaiting && scans.length > 0 && (
          <div className="return-receive__scans">
            <h3>Scans</h3>
            {[...scans].reverse().map((scan) => (
              <div key={scan.id} className="return-receive__scan">
                <span>
                  {lineName(lineById.get(scan.itemId))}
                  <small>
                    {scan.serialNumber ? `SN ${scan.serialNumber}` : scan.code || scan.gtin}
                    {scan.lot ? ` · Lot ${scan.lot}` : ''} · {scan.quantity}
                  </small>
                </span>
                <button type="button" className="receive-line__action" onClick={() => remove(scan.id)}>
                  <FiRotateCcw aria-hidden /> Undo
                </button>
              </div>
            ))}
          </div>
        )}

        {awaiting && (
          <div className="purchase-order-receive__footer">
            <Button
              type="primary"
              fullWidth
              disabled={totalScanned === 0 || busy}
              {...eqProps({ onClick: () => setConfirmOpen(true) })}
            >
              Complete Receive
            </Button>
            {totalScanned === 0 && (
              <p className="purchase-order-receive__blocked">
                Scan the products first. Nothing can be received until they are scanned in.
              </p>
            )}
          </div>
        )}
      </div>

      <div className={['result-panel', (confirmOpen || completed) && 'open'].filter(Boolean).join(' ')}>
        {confirmOpen && !completed && (
          <>
            <div className="result-message">
              {shortLines.length > 0 ? (
                <FiAlertTriangle style={{ color: 'var(--y500)' }} />
              ) : (
                <FiCheckCircle style={{ color: 'var(--green)' }} />
              )}
              <h1>{shortLines.length > 0 ? 'Receive short?' : 'Complete receive?'}</h1>
              <p>
                {totalScanned} of {totalRma} units on RMA #{rma} were scanned.
                {shortLines.length > 0 &&
                  ` Short: ${shortLines
                    .map((line) => `${lineName(line)} ${line.rmaQuantity - line.scannedQuantity}`)
                    .join(', ')}. The return is received at the scanned quantities and the shortfall is flagged.`}
              </p>
            </div>
            <div className="result-actions">
              <Button type="primary" fullWidth disabled={busy} {...eqProps({ onClick: complete })}>
                Complete Receive
              </Button>
              <Button type="default" fullWidth {...eqProps({ onClick: () => setConfirmOpen(false) })}>
                Keep scanning
              </Button>
            </div>
          </>
        )}
        {completed && (
          <>
            <div className="result-message">
              <FiCheckCircle style={{ color: 'var(--green)' }} />
              <h1>Return received</h1>
              <p>
                RMA #{rma} was received
                {clientReturn.receivingShortfall ? ' short' : ''}. Its return order is ready to
                receive and stock.
              </p>
            </div>
            <div className="result-actions">
              <Button type="primary" fullWidth {...eqProps({ onClick: () => navigate('/') })}>
                Done
              </Button>
            </div>
          </>
        )}
      </div>

      {busy && (
        <div className="blocking-spinner-container" onClick={(event) => event.stopPropagation()}>
          <FadeLoader color="var(--n0)" />
        </div>
      )}
    </Page>
  );
}

export default ReturnReceive;
